/**
 * 1.8.4 §3 (D-1.8.4-3) — Today's lines in words: each with its program word (never a colour alone) and its time.
 *
 * The day is the house calendar's lines for that day (shared/house-calendar.ts, the words of 1.8.2's lineWords) with,
 * for the office roles, Housekeeping's count line replaced by each of the day's housekeeping visits. Each line knows
 * where it is changed ("Open in Housekeeping") — Today itself changes nothing.
 */
import type { TFunction } from 'i18next';
import { dayOverview, type CalCategory, type CalLine } from '@shared/house-calendar';
import { stripProgramOf, type HouseAttnLine, type HouseEmail, type HouseProgram, type HouseReport, type HouseVisit } from '@shared/house-today';
import { clockWithPeriod } from '@shared/dayplan';
import { serviceMinutesNow } from '@shared/dates';
import { linkWords, timeWords, whatWords } from '@/features/calendar/lineWords';

type Lang = 'en' | 'es';

/** One line of the day, as Today draws it. */
export interface DayItem {
  id: string;
  category: CalCategory;
  minutes: number | null;
  endMinutes: number | null;
  /** What it is, without its program word or time. */
  what: string;
  /** The names on it, as the role may see them (null: none). */
  who: string | null;
  /** Call (the number is never printed): only where the role may call. */
  phone: string | null;
  callName: string;
  href: string | null;
  /** The strip's tab the address opens in. */
  openIn: 'housekeeping' | 'companion' | 'reading' | null;
}

/** The program word of a category: Housekeeping · Companion · Reading & Services · Tech Support · Events · Closed · Holidays. */
export const categoryWord = (c: CalCategory, t: TFunction) => t(`cal182.cat.${c}`);
export const programWord = (p: HouseProgram, t: TFunction) => t(`today184.prog.${p}`);

const STATUS_KEY: Record<string, string> = {
  Proposed: 'status.proposed',
  Scheduled: 'status.scheduled',
  'On Hold': 'status.onHold',
  'In Progress': 'status.inProgress',
  Completed: 'status.completed',
  'No Show': 'status.noShow',
  'Unable to Complete': 'status.unable',
};

/** "TEST-03C · SECURITY-TEST Tenant Hotel · Scheduled · not confirmed". */
export function visitWhat(v: HouseVisit, t: TFunction): string {
  const parts = [v.unit, v.tenantName].filter(Boolean);
  const status = STATUS_KEY[v.status];
  parts.push(status ? t(status) : v.status);
  if (v.status === 'Scheduled' && v.kind === 'tenant') parts.push(v.confirmation === 'Confirmed' ? t('status.confirmed') : t('today184.notConfirmed'));
  return parts.join(' · ');
}

function calItem(l: CalLine, t: TFunction, lang: Lang): DayItem {
  const names = [l.tenantName, l.volunteerFirst].filter((x): x is string => !!x);
  return {
    id: l.id,
    category: l.category,
    minutes: l.minutes,
    endMinutes: l.endMinutes,
    what: whatWords(l, t, lang),
    who: names.length ? names.join(' · ') : null,
    phone: null,
    callName: '',
    href: l.href,
    openIn: l.category === 'housekeeping' || l.category === 'companion' || l.category === 'reading' ? l.category : null,
  };
}

function visitItem(v: HouseVisit, t: TFunction): DayItem {
  return {
    id: `hkv-${v.appointmentId}`,
    category: 'housekeeping',
    minutes: v.minutes,
    endMinutes: null,
    what: visitWhat(v, t),
    who: v.tenantName || null,
    phone: v.phone,
    callName: [v.unit, v.tenantName].filter(Boolean).join(' · '),
    href: v.href,
    openIn: 'housekeeping',
  };
}

/**
 * The day's items in time order: the whole-day lines first (holidays, closed), then by time. `visits` is null when
 * the role does not get them (the viewer): Housekeeping's count line then stands.
 */
export function dayItems(lines: readonly CalLine[], dayKey: string, visits: readonly HouseVisit[] | null, t: TFunction, lang: Lang): DayItem[] {
  const cal = dayOverview(lines, dayKey).filter((l) => visits === null || l.category !== 'housekeeping');
  const items = [...cal.map((l) => calItem(l, t, lang)), ...(visits ?? []).map((v) => visitItem(v, t))];
  const holidayFirst = (i: DayItem) => (i.category === 'holidays' ? 0 : 1);
  return items.sort((a, b) => {
    if (a.minutes === null || b.minutes === null) return (a.minutes === null ? 0 : 1) - (b.minutes === null ? 0 : 1) || holidayFirst(a) - holidayFirst(b);
    return a.minutes - b.minutes || (a.category === 'housekeeping' ? -1 : 0) - (b.category === 'housekeeping' ? -1 : 0) || a.id.localeCompare(b.id);
  });
}

/** "9:30 AM", or "All day". */
export const itemTime = (i: Pick<DayItem, 'minutes'>, t: TFunction, lang: Lang) => timeWords({ minutes: i.minutes } as CalLine, t, lang);

/** The words of the button that goes where a line is changed: "Open in Housekeeping" · "Open in Events" · "Open Settings". */
export function openWords(i: Pick<DayItem, 'category' | 'href'>, t: TFunction): string {
  if (i.category === 'closed' || i.category === 'holidays') return i.href ? linkWords(i.href, t) : '';
  return t('today184.openIn', { program: categoryWord(i.category, t) });
}

/* ------------------------------------------------------------------------------------------------
 * Needs your attention, the emails, the reports
 * ---------------------------------------------------------------------------------------------- */

export const attnWords = (l: HouseAttnLine, t: TFunction) =>
  // 1.8.5 (D-1.8.5-3, -4): the Tech Support lines' own words
  l.kind === 'techNew' ? t('visit185.attnNew', { count: l.count }) : l.kind === 'needsNewDay' && l.program === 'tech' ? t('visit185.attnNewDay', { count: l.count }) : t(`today184.attn.${l.kind}`, { count: l.count });
/** Where an attention line opens: its strip tab's word, or the page's ("Open in Events"). */
export function attnOpenWords(l: HouseAttnLine, t: TFunction): string {
  if (l.kind === 'inbox' || l.kind === 'emailFailed') return t('inbox1023.openIn');
  const tab = l.openIn ?? stripProgramOf(l.program);
  return t('today184.openIn', { program: tab ? programWord(tab, t) : programWord(l.program, t) });
}

/** "Sent 9:00 AM — Fall Fest Celebration reminder — 42 tenants, 6 volunteers" · "Goes out at 2:00 PM — …" · "Cancelled by Caroline — …". */
export function emailWords(e: HouseEmail, t: TFunction, lang: Lang): string {
  const time = clockWithPeriod(serviceMinutesNow(new Date(e.at)), lang);
  const what = t('today184.email.what', { event: e.eventName });
  let state: string;
  switch (e.state) {
    case 'sent':
      state = t('today184.email.sent', { time });
      break;
    case 'scheduled':
      state = t('today184.email.scheduled', { time });
      break;
    case 'cancelled':
      state =
        e.cancelledWhy === 'settings-off'
          ? t('today184.email.cancelledSettings')
          : e.cancelledWhy === 'event-cancelled'
            ? t('today184.email.cancelledEvent')
            : e.cancelledBy
              ? t('today184.email.cancelledBy', { name: e.cancelledBy })
              : t('today184.email.cancelledPerson');
      break;
    case 'notSent':
      state = t('today184.email.notSent', { time });
      break;
    default:
      state = t('today184.email.failed', { time });
  }
  const reached = e.state === 'sent' && e.tenants !== null ? [t('today184.email.tenants', { count: e.tenants }), ...(e.volunteers ? [t('today184.email.volunteers', { count: e.volunteers })] : [])].join(', ') : null;
  return [state, what, reached].filter(Boolean).join(' — ');
}

export function reportWords(r: HouseReport, t: TFunction, dayWords: string): string {
  return [t(`today184.report.${r.kind}`), r.program ? programWord(r.program, t) : null, dayWords, r.unit, r.tenantName].filter(Boolean).join(' · ');
}
